import { Component, OnInit, inject, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { catchError, of } from 'rxjs';
import { FormDropdownService } from '../../../../../core/services/form-dropdown.service';
import { GeographyService } from '../../../../../core/services/geography.service';
import { OperatingSiteVM } from './operational-sites.model';

interface DropdownOption {
  label: string;
  value: string;
}

/**
 * Blocco indirizzo di una singola sede operativa (7 campi + cascade
 * Regione -> Provincia -> Comune). Condiviso da tutte le form "Dati Specifici".
 *
 * Il view-model `site` è la sorgente di verità: i campi vengono scritti
 * direttamente sull'oggetto (mutazione) così che il form padre lo legga al
 * momento del salvataggio; i segnali locali servono solo alla reattività della view.
 * Le opzioni provincia/comune vengono idratate una sola volta in `ngOnInit`
 * (una istanza per sede grazie all'`@for`, quindi nessun effetto/loop).
 */
@Component({
  selector: 'app-operating-site-form',
  standalone: true,
  imports: [CommonModule, FormsModule, InputTextModule, SelectModule],
  templateUrl: './operating-site-form.component.html',
})
export class OperatingSiteFormComponent implements OnInit {
  site = input.required<OperatingSiteVM>();
  readonly = input<boolean>(false);
  formSubmitted = input<boolean>(false);

  private formDropdownService = inject(FormDropdownService);
  private geographyService = inject(GeographyService);

  // Segnali locali per la reattività della view (mirror del view-model).
  streetType = signal('');
  streetName = signal('');
  streetNumber = signal('');
  zipCode = signal('');
  region = signal('');
  province = signal('');
  municipality = signal('');

  streetTypeOptions = signal<DropdownOption[]>([]);
  regionOptions = signal<DropdownOption[]>([]);
  provinceOptions = signal<DropdownOption[]>([]);
  municipalityOptions = signal<DropdownOption[]>([]);

  ngOnInit(): void {
    const s = this.site();
    this.streetType.set(s.streetType);
    this.streetName.set(s.streetName);
    this.streetNumber.set(s.streetNumber);
    this.zipCode.set(s.zipCode);
    this.region.set(s.region);
    this.province.set(s.province);
    this.municipality.set(s.municipality);

    this.loadStreetTypes();
    this.loadRegions();
    if (s.region) this.fetchProvinces(s.region);
    if (s.province) this.fetchMunicipalities(s.province);
  }

  private loadStreetTypes(): void {
    this.formDropdownService
      .getFormDropdownValues('PLACE_NAME', 'PLACE_NAME')
      .pipe(catchError(() => of({ status: false, message: '', data: [] })))
      .subscribe((response) => {
        this.streetTypeOptions.set(
          (response.data || []).map((item) => ({
            label: item.valueDescription,
            value: item.valueKey,
          })),
        );
      });
  }

  private loadRegions(): void {
    this.geographyService
      .getRegions()
      .pipe(catchError(() => of([])))
      .subscribe((regions) => {
        this.regionOptions.set(
          (regions || []).map((item: any) => ({ label: item.name, value: item.code })),
        );
      });
  }

  private fetchProvinces(region: string): void {
    this.geographyService
      .getProvinces(region)
      .pipe(catchError(() => of([])))
      .subscribe((provinces: any[]) => {
        this.provinceOptions.set(
          (provinces || []).map((item: any) => ({ label: item.name, value: item.code })),
        );
      });
  }

  private fetchMunicipalities(province: string): void {
    this.geographyService
      .getMunicipalities(province)
      .pipe(catchError(() => of([])))
      .subscribe((municipalities: any[]) => {
        this.municipalityOptions.set(
          (municipalities || []).map((item: any) => ({ label: item.name, value: item.code })),
        );
      });
  }

  setStreetType(v: string): void {
    this.streetType.set(v);
    this.site().streetType = v;
  }

  setStreetName(v: string): void {
    this.streetName.set(v);
    this.site().streetName = v;
  }

  setStreetNumber(v: string): void {
    this.streetNumber.set(v);
    this.site().streetNumber = v;
  }

  setZipCode(v: string): void {
    this.zipCode.set(v);
    this.site().zipCode = v;
  }

  setMunicipality(v: string): void {
    this.municipality.set(v);
    this.site().municipality = v;
  }

  onRegionChange(v: string): void {
    this.region.set(v);
    this.site().region = v;
    // Reset dei livelli dipendenti.
    this.province.set('');
    this.site().province = '';
    this.municipality.set('');
    this.site().municipality = '';
    this.provinceOptions.set([]);
    this.municipalityOptions.set([]);
    if (v) this.fetchProvinces(v);
  }

  onProvinceChange(v: string): void {
    this.province.set(v);
    this.site().province = v;
    this.municipality.set('');
    this.site().municipality = '';
    this.municipalityOptions.set([]);
    if (v) this.fetchMunicipalities(v);
  }

  isValidZipCode(zipCode: string): boolean {
    if (!zipCode) return true;
    return /^\d{5}$/.test(zipCode);
  }
}
